import { useEffect, useState } from 'react';

import { useAuthentication } from '@ravago/shared/authentication';
import { getCallOffOrderDetail } from '@ravago/shared/next-data-access/client';
import { SystemPageType } from '@ravago/shared/page-data/enums/system-page-type';
import { CallOffOrderColor, CallOffOrderDetail } from '@ravago/shared/page-data/models/elements';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { InputField } from '@ravago/shared/radiance/components/input-field/components/InputField/InputField';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { usePathLocale } from '../../../../../hooks/use-path-locale';
import { CallOffProgressBar } from '../../../../shared/order/call-off-progress-bar';
import { SystemPageLink } from '../../../system-page-link/system-page-link';

interface Props {
   id: string;
   quantityAmount: number | undefined;
   callOffOrderColor: CallOffOrderColor;
   usesAgreementApp: boolean;
   callOffAgreementRoute?: string;
   onQuantityAmountChanged: (quantity: number | undefined) => void;
}

export const DynamicProductBlockCallOffFields = ({
   id,
   quantityAmount,
   callOffOrderColor,
   onQuantityAmountChanged,
   usesAgreementApp,
   callOffAgreementRoute
}: Props) => {
   const translate = useTranslations();
   const format = useFormatter();
   const locale = usePathLocale();
   const { accessToken } = useAuthentication();

   const [callOffOrderDetail, setCallOffOrderDetail] = useState<CallOffOrderDetail | undefined>();

   const quantityValue = !quantityAmount ? '' : quantityAmount;

   const isQuantityInvalid = !!quantityValue && quantityValue > callOffOrderColor.recommendedCallOff.remainingQuantity.amount;

   const totalPriceAmount = quantityValue ? callOffOrderColor.recommendedCallOff.unitPrice.price.amount * quantityValue : 0;

   useEffect(() => {
      if (!accessToken) return;

      const fetchCallOffOrderDetail = async (callOffOrderId: string) => {
         const callOffOrder = await getCallOffOrderDetail(callOffOrderId);
         if (callOffOrder) {
            setCallOffOrderDetail(callOffOrder);
         }
      };

      fetchCallOffOrderDetail(callOffOrderColor.recommendedCallOff.id);
   }, [accessToken, callOffOrderColor]);

   const handleQuantityChange = (newQuantityValue: string) => {
      if (newQuantityValue !== '') {
         onQuantityAmountChanged(Math.floor(Number(newQuantityValue)));
      } else {
         onQuantityAmountChanged(undefined);
      }
   };

   return (
      <Box direction="column" gap="md">
         <Typography variant="body-highlight" id="call-off-quantity-label" color="subtle">
            {translate('request-for-quotation.quantity-label')}
         </Typography>
         <InputField
            id={`${id}-quantity-input`}
            label={translate('request-for-quotation.quantity-label')}
            suffix={callOffOrderColor.recommendedCallOff.unitPrice.unitOfMeasure}
            type="number"
            min={1}
            value={quantityValue?.toString()}
            onChange={handleQuantityChange}
            error={isQuantityInvalid}
            errorMessage={translate('request-for-quotation.invalid-quantity')}
         />
         {usesAgreementApp ? (
            <Box direction="column" gap="xs">
               {callOffOrderDetail && <CallOffProgressBar callOffOrderDetail={callOffOrderDetail} showSupportingText={false} showLabel={false} />}
               <Box justify="between">
                  <Typography color="primary">
                     {translate('request-for-quotation.quantity-delivered', {
                        quantity: format.quantity(callOffOrderColor.recommendedCallOff.consumedQuantity)
                     })}
                  </Typography>
                  <Typography color="subtle">
                     {translate('request-for-quotation.quantity-remaining', {
                        quantity: format.quantity(callOffOrderColor.recommendedCallOff.remainingQuantity)
                     })}
                  </Typography>
               </Box>
               <Box justify="between" items="center" spacing={{ top: 'md' }}>
                  <Typography>{translate('my-orders.call-off')}</Typography>
                  {callOffAgreementRoute ? (
                     <SystemPageLink
                        baseRoute={callOffAgreementRoute}
                        locale={locale}
                        type={SystemPageType.CALL_OFF_ORDER_DETAIL}
                        slug={callOffOrderColor.recommendedCallOff.id}
                        target="_blank"
                        underline="always"
                        fullWidth={false}
                     >
                        <Badge trailingIcon="arrow-up-right-from-square">{callOffOrderColor.recommendedCallOff.id}</Badge>
                     </SystemPageLink>
                  ) : (
                     <Badge>{callOffOrderColor.recommendedCallOff.id}</Badge>
                  )}
               </Box>
               <Box justify="between">
                  <Typography>{translate('my-orders.unit-price')}</Typography>
                  <Typography variant="body-highlight">{format.unitPrice(callOffOrderColor.recommendedCallOff.unitPrice)}</Typography>
               </Box>
               <Divider />
            </Box>
         ) : (
            <Box justify="between">
               <Typography>{translate('my-orders.remaining-quantity')}</Typography>
               <Typography variant="body-highlight">{format.quantity(callOffOrderColor.recommendedCallOff.totalQuantity)}</Typography>
            </Box>
         )}
         <Box justify="between">
            <Typography>{translate('request-for-quotation.total-price')}:</Typography>
            <Typography variant="body-highlight">
               {format.price({
                  amount: totalPriceAmount,
                  currency: callOffOrderColor.recommendedCallOff.unitPrice.price.currency
               })}
            </Typography>
         </Box>
      </Box>
   );
};
